/**
 * CollectionsTab — Recordatorios de cobranza con detalle, acciones y facturas vencidas.
 */
import { useState } from 'react'
import { useArOverdue, useCollectionReminders, useCreateReminder, type OverdueInvoice, type CollectionReminder } from '../hooks/useFinance'
import { useTenantLocale, formatNumber, formatDate } from '@/lib/locale'
import { DataTable } from '@/components/ds/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { usePermission } from '@/hooks/usePermission'
import { ToastContainer, useToast } from '@/components/Toast'
import { extractErrorMessage } from '@/lib/errorUtils'
import { AlertTriangle, Mail, Send, RefreshCw, CheckCircle, Clock, Eye, User, FileText, Calendar, MessageSquare, AtSign } from 'lucide-react'
import { Modal } from '@/components/ds/Modal'

const TYPE_CONFIG: Record<string, { label: string; cls: string; icon: typeof Mail }> = {
  friendly: { label: 'Amistoso',         cls: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',     icon: Mail },
  firm:     { label: 'Firme',            cls: 'bg-warning-surface-strong text-warning-content',   icon: Mail },
  final:    { label: 'Definitivo',       cls: 'bg-danger-surface-strong text-danger-content',       icon: AlertTriangle },
  pre_due:  { label: 'Pre-vencimiento',  cls: 'bg-info-surface-strong text-info-content', icon: Clock },
}

const STATUS_CONFIG: Record<string, { label: string; cls: string; icon: typeof CheckCircle }> = {
  sent:   { label: 'Enviado', cls: 'text-success-content-soft', icon: CheckCircle },
  failed: { label: 'Fallido', cls: 'text-danger-content-soft', icon: AlertTriangle },
}

export default function CollectionsTab() {
  const { data: overdueData, isLoading: loadingOverdue } = useArOverdue()
  const { data: remindersData, isLoading: loadingReminders } = useCollectionReminders()
  const { mutate: createReminder, isPending: isSending } = useCreateReminder()
  const { can } = usePermission()
  const locale = useTenantLocale()
  const toast = useToast()
  const [detailReminder, setDetailReminder] = useState<CollectionReminder | null>(null)

  const overdue = overdueData?.invoices || []
  const reminders = remindersData?.reminders || []

  const fmt = (value: string) => {
    const n = parseFloat(value)
    const decimals = n % 1 !== 0 ? 2 : 0
    return `$${formatNumber(n, locale, decimals)}`
  }

  const handleResend = (r: CollectionReminder) => {
    createReminder({
      customer: r.customer,
      sale_document: r.sale_document,
      reminder_type: r.reminder_type,
      channel: 'email',
    }, {
      onSuccess: () => toast.success('Recordatorio reenviado'),
      onError: (e: any) => toast.error(extractErrorMessage(e, 'Error al reenviar')),
    })
  }

  const handleSendManual = (inv: OverdueInvoice) => {
    if (!inv.document_id) return
    createReminder({
      customer: (inv as any).customer_id || '',
      sale_document: inv.document_id,
      reminder_type: 'friendly',
      channel: 'email',
    }, {
      onSuccess: () => toast.success('Recordatorio enviado'),
      onError: (e: any) => toast.error(extractErrorMessage(e, 'Error al enviar')),
    })
  }

  return (
    <div className="space-y-6">
      {/* ═══ Facturas vencidas ═══ */}
      <div>
        <h3 className="text-sm font-bold text-content mb-3 flex items-center gap-2">
          <AlertTriangle size={16} className="text-danger-solid" />
          Facturas vencidas ({overdueData?.count || 0})
        </h3>
        <DataTable
          columns={[
            { key: 'customer_name', header: 'Cliente', render: (i: OverdueInvoice) => <span className="font-medium text-content">{i.customer_name}</span> },
            { key: 'document_type', header: 'Tipo' },
            { key: 'due_date', header: 'Vencimiento', render: (i: OverdueInvoice) => formatDate(i.due_date, locale) },
            { key: 'total', header: 'Monto', numeric: true, render: (i: OverdueInvoice) => fmt(i.total) },
            {
              key: 'days_overdue', header: 'Días mora', numeric: true,
              render: (i: OverdueInvoice) => (
                <span className={`font-semibold ${i.days_overdue > 30 ? 'text-danger-content-soft' : 'text-warning-content-soft'}`}>
                  {i.days_overdue}d
                </span>
              ),
            },
            // La columna por permiso se resuelve en el ARRAY y no con un `<th>`
            // condicional: así el encabezado y la celda no se pueden
            // desincronizar, que es el bug clásico de ese patrón.
            ...(can('receivables.collections', 'create')
              ? [{
                  key: 'accion', header: 'Acción', width: 80,
                  render: (i: OverdueInvoice) => (
                    <button
                      onClick={() => handleSendManual(i)}
                      disabled={isSending}
                      title="Enviar recordatorio"
                      className="p-1.5 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 text-blue-500 disabled:opacity-40"
                    >
                      <Send size={14} />
                    </button>
                  ),
                }]
              : []),
          ]}
          rows={overdue}
          // `document_id` es opcional en el tipo: el índice es el respaldo.
          rowKey={(i: OverdueInvoice, idx: number) => i.document_id ?? idx}
          loading={loadingOverdue}
          empty={<EmptyState icon={Send} size="sm" title="Sin facturas vencidas." />}
          minWidth={720}
          caption="Facturas vencidas por cobrar"
        />
      </div>

      {/* ═══ Recordatorios enviados ═══ */}
      <div>
        <h3 className="text-sm font-bold text-content mb-3 flex items-center gap-2">
          <Mail size={16} className="text-primary" />
          Recordatorios enviados ({reminders.length})
        </h3>
        <DataTable
          columns={[
            { key: 'customer_name', header: 'Cliente', render: (r: CollectionReminder) => <span className="font-medium text-content">{r.customer_name}</span> },
            {
              key: 'documento', header: 'Documento',
              render: (r: CollectionReminder) => (
                <>
                  {r.document_type || ''} {r.document_folio ? `#${r.document_folio}` : ''}
                  {r.document_total && <span className="ml-2 text-xs text-content-subtle">{fmt(String(r.document_total))}</span>}
                </>
              ),
            },
            {
              key: 'reminder_type', header: 'Tipo',
              render: (r: CollectionReminder) => {
                const conf = TYPE_CONFIG[r.reminder_type] || TYPE_CONFIG.friendly
                return <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${conf.cls}`}>{r.reminder_type_display || conf.label}</span>
              },
            },
            {
              key: 'channel', header: 'Canal',
              render: (r: CollectionReminder) => r.channel === 'email'
                ? <span className="inline-flex items-center gap-1 text-xs"><Mail size={12} /> Email</span>
                : <span className="inline-flex items-center gap-1 text-xs"><MessageSquare size={12} /> Interno</span>,
            },
            {
              key: 'status', header: 'Estado',
              render: (r: CollectionReminder) => {
                const conf = STATUS_CONFIG[r.status] || STATUS_CONFIG.sent
                const Icon = conf.icon
                return <span className={`inline-flex items-center gap-1 text-xs font-semibold ${conf.cls}`}><Icon size={12} /> {conf.label}</span>
              },
            },
            {
              key: 'sent_at', header: 'Enviado',
              render: (r: CollectionReminder) => (
                <span className="text-xs text-content-subtle">
                  {new Date(r.sent_at).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' })}
                </span>
              ),
            },
            {
              key: 'acciones', header: 'Acciones', width: 96,
              render: (r: CollectionReminder) => (
                <div className="inline-flex items-center gap-1">
                  <button onClick={() => setDetailReminder(r)} title="Ver detalle" className="p-1.5 rounded-lg hover:bg-soft text-content-muted">
                    <Eye size={14} />
                  </button>
                  {can('receivables.reminders', 'create') && (
                    <button
                      onClick={() => handleResend(r)}
                      disabled={isSending}
                      title="Reenviar recordatorio"
                      className="p-1.5 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 text-blue-500 disabled:opacity-40"
                    >
                      <RefreshCw size={14} />
                    </button>
                  )}
                </div>
              ),
            },
          ]}
          rows={reminders}
          rowKey={(r: CollectionReminder) => r.id}
          loading={loadingReminders}
          empty={<EmptyState icon={Mail} size="sm" title="Sin recordatorios enviados." />}
          minWidth={900}
          caption="Recordatorios de cobranza enviados"
        />
      </div>

      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />

      {/* ═══ Modal detalle recordatorio ═══ */}
      {detailReminder && (() => {
        const r = detailReminder
        const typeConf = TYPE_CONFIG[r.reminder_type] || TYPE_CONFIG.friendly
        const statusConf = STATUS_CONFIG[r.status] || STATUS_CONFIG.sent
        const StatusIcon = statusConf.icon
        return (
          <Modal open onClose={() => setDetailReminder(null)} size="sm" title="Detalle del Recordatorio">
            <div className="space-y-4 text-sm">

              {/* Tipo + estado */}
              <div className="flex items-center gap-3">
                <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${typeConf.cls}`}>
                  {r.reminder_type_display}
                </span>
                <span className={`inline-flex items-center gap-1 text-xs font-semibold ${statusConf.cls}`}>
                  <StatusIcon size={12} /> {statusConf.label}
                </span>
                <span className="text-xs text-content-subtle ml-auto">
                  {r.channel === 'email' ? (
                    <span className="inline-flex items-center gap-1"><Mail size={12} /> Email</span>
                  ) : (
                    <span className="inline-flex items-center gap-1"><MessageSquare size={12} /> Notificación interna</span>
                  )}
                </span>
              </div>

              <hr className="border-divider" />

              {/* Cliente */}
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-content-muted uppercase tracking-wide">A quién se envió</p>
                <div className="flex items-start gap-2 text-content">
                  <User size={15} className="mt-0.5 text-content-subtle shrink-0" />
                  <div>
                    <p className="font-semibold">{r.customer_name}</p>
                    {r.customer_tax_id && <p className="text-xs text-content-subtle">{r.customer_tax_id}</p>}
                  </div>
                </div>
                {r.customer_email && (
                  <div className="flex items-center gap-2 text-content-muted">
                    <AtSign size={14} className="text-content-subtle shrink-0" />
                    <span className="text-xs">{r.customer_email}</span>
                  </div>
                )}
              </div>

              <hr className="border-divider" />

              {/* Documento */}
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-content-muted uppercase tracking-wide">Documento relacionado</p>
                <div className="flex items-start gap-2 text-content">
                  <FileText size={15} className="mt-0.5 text-content-subtle shrink-0" />
                  <div className="flex-1">
                    <p className="font-semibold">
                      {r.document_type} {r.document_folio ? `#${r.document_folio}` : ''}
                    </p>
                    {r.document_total && (
                      <p className="text-xs text-content-muted">{fmt(String(r.document_total))}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-6 text-xs text-content-muted pl-6">
                  {r.document_issue_date && <span>Emisión: {formatDate(r.document_issue_date, locale)}</span>}
                  {r.document_due_date && <span>Vence: {formatDate(r.document_due_date, locale)}</span>}
                </div>
              </div>

              <hr className="border-divider" />

              {/* Enviado por / cuando */}
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-content-muted uppercase tracking-wide">Enviado</p>
                <div className="flex items-center gap-2 text-content">
                  <Calendar size={14} className="text-content-subtle shrink-0" />
                  <span>{new Date(r.sent_at).toLocaleString('es-CL', { dateStyle: 'long', timeStyle: 'short' })}</span>
                </div>
                <div className="flex items-center gap-2 text-content-muted text-xs">
                  <User size={13} className="text-content-subtle shrink-0" />
                  <span>Por: {r.sent_by_name}</span>
                </div>
              </div>

              {/* Notas */}
              {r.notes && (
                <>
                  <hr className="border-divider" />
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-content-muted uppercase tracking-wide">Notas</p>
                    <p className="text-content whitespace-pre-wrap text-xs bg-surface-muted rounded-lg px-3 py-2">{r.notes}</p>
                  </div>
                </>
              )}
            </div>
          </Modal>
        )
      })()}
    </div>
  )
}
